import "@/App.css";
import PropTypes from "prop-types";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { SETTINGS_EXTENDED_FEATURES_ENABLED, CONTENT_ENABLED } from "@/lib/featureFlags";
import { AppShell } from "@/components/shell/AppShell";
import { AppStoreProvider, useAppStore } from "@/store/useAppStore";
import { useOnboardingStore } from "@/store/useOnboardingStore";
import { AuthSessionBridge } from "@/components/auth/AuthSessionBridge";
import { SessionActivityBridge } from "@/components/auth/SessionActivityBridge";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { GuestOnly } from "@/components/auth/GuestOnly";
import { Loader2 } from "lucide-react";

import LoginPage from "@/pages/auth/LoginPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";

import BackendInbox from "@/pages/inbound/BackendInbox";
import { OutboundDashboard, OutboundCampaigns, SequenceBuilder } from "@/pages/outbound/OutboundPages";
import { CampaignWizard } from "@/pages/outbound/CampaignWizard";
import { CampaignEditor } from "@/pages/outbound/CampaignEditor";
import { OutreachTemplatesPage } from "@/pages/outbound/OutreachTemplatesPage";
import { KnowledgeOverview, BrainDocumentsRoute, ChunkingConfig, RagPlayground } from "@/pages/knowledge/KnowledgePages";
import VoiceMemosPage from "@/pages/knowledge/VoiceMemosPage";
import { MockDataRemovedPage } from "@/components/common/MockDataRemovedPage";
import { SignupRouteGuard } from "@/components/auth/SignupRouteGuard";
import BackendPeoplePage from "@/pages/people/BackendPeoplePage";
import BackendTodayPage from "@/pages/today/BackendTodayPage";
import SignalsPage from "@/pages/signals/SignalsPage";
import SignalsSourcesPage from "@/pages/signals/SignalsSourcesPage";
import SignalDetailPage from "@/pages/signals/SignalDetailPage";
import RncDashboard from "@/pages/signals/rnc/RncDashboard";
import RncDetailPage from "@/pages/signals/rnc/RncDetailPage";
import RncWorkflowViewer from "@/pages/signals/rnc/RncWorkflowViewer";
import AbmDetailPage from "@/pages/signals/abm/AbmDetailPage";
import AbmWorkflowViewer from "@/pages/signals/abm/AbmWorkflowViewer";
import WorkflowAnalyticsPage from "@/pages/signals/rnc/WorkflowAnalyticsPage";
import OutreachPage from "@/pages/outbound/OutreachPage";
import LeadReportsPage from "@/pages/reports/LeadReportsPage";
import UsagePage from "@/pages/usage/UsagePage";
import OnboardingWizard from "@/pages/onboarding/OnboardingWizard";
import { MarketingSaaS, MarketingCloud } from "@/pages/marketing/MarketingLandings";
import UniversalLanding from "@/pages/marketing/UniversalLanding";
import StatusPage from "@/pages/status/StatusPage";
import { PrivacyPage, TermsPage, SecurityPage } from "@/pages/legal/LegalPages";
import SettingsReferrals from "@/pages/settings/SettingsReferrals";
import UpgradePage from "@/pages/upgrade/UpgradePage";
import MockCheckoutPage from "@/pages/billing/MockCheckoutPage";
import { SignupPage, ProvisioningPage, InstanceReadyPage, WelcomePage } from "@/pages/signup/FunnelPages";
import { SettingsTeam, SettingsInstance, SettingsBilling, SettingsAccount, SettingsPersona, SettingsConnections, SettingsGeneral, SettingsSafety, SettingsPrivacy, SettingsPerformance, SettingsAudit, SettingsRouting, ToInbox, ToPeople, ToDeals, ToOutreach, ToBrain } from '@/pages/settings/SettingsPages';
import { SettingsAiAgents } from '@/pages/settings/AiAgentsPage';
import AcceptInvitePage from "@/pages/auth/AcceptInvitePage";

import "@/components/tour/tour.css";
import { Toaster } from "@/components/ui/sonner";

function ContentPlaceholder() {
  return (
    <MockDataRemovedPage
      title="Content"
      description="Plan, draft, and schedule content from your connected channels."
    />
  );
}

function LegacyResetPasswordRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/reset-password${search}`} replace />;
}

function DesignCampaignRedirect() {
  const { id } = useParams();
  return <Navigate to={`/outreach/campaigns/${id}`} replace />;
}

function RootRedirect() {
  const { isAuthed, authReady } = useAppStore();
  const onboardingCompleted = useOnboardingStore((s) => s.completed);
  const currentStep = useOnboardingStore((s) => s.currentStep);
  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="animate-spin text-indigo-600" size={28} aria-hidden />
      </div>
    );
  }
  if (isAuthed && !onboardingCompleted) {
    const step = Math.max(1, Number(currentStep) || 1);
    return <Navigate to={`/onboarding/${step}`} replace />;
  }
  if (isAuthed) return <Navigate to="/today" replace />;
  return <UniversalLanding />;
}

function SettingsExtendedRoute({ children }) {
  if (!SETTINGS_EXTENDED_FEATURES_ENABLED) return <Navigate to="/settings/team" replace />;
  return children;
}
SettingsExtendedRoute.propTypes = {
  children: PropTypes.node,
};

SettingsExtendedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

function SettingsHomeRedirect() {
  return <Navigate to="/settings/team" replace />;
}

function ContentRoute({ children }) {
  if (!CONTENT_ENABLED) return <Navigate to="/today" replace />;
  return children;
}
ContentRoute.propTypes = {
  children: PropTypes.node,
};

ContentRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

function App() {
  return (
    <AppStoreProvider>
      <BrowserRouter>
        <AuthSessionBridge />
        <SessionActivityBridge />
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/demo" element={<Navigate to="/login" replace />} />
          <Route path="/demo/:persona" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/reset-password" element={<LegacyResetPasswordRedirect />} />
          <Route path="/tenant-select" element={<Navigate to="/today" replace />} />
          <Route path="/workspace-select" element={<Navigate to="/today" replace />} />
          <Route path="/signup" element={<SignupRouteGuard page={SignupPage} />} />
          <Route path="/provisioning/:reservationId" element={<SignupRouteGuard page={ProvisioningPage} />} />
          <Route path="/instance-ready/:reservationId" element={<SignupRouteGuard page={InstanceReadyPage} />} />
          <Route path="/welcome" element={<SignupRouteGuard page={WelcomePage} />} />
          <Route path="/onboarding" element={<Navigate to="/onboarding/welcome" replace />} />
          <Route path="/onboarding/welcome" element={<OnboardingWizard />} />
          <Route path="/onboarding/:step" element={<OnboardingWizard />} />
          <Route path="/marketing" element={<Navigate to="/marketing/saas" replace />} />
          <Route path="/marketing/saas" element={<MarketingSaaS />} />
          <Route path="/marketing/hardware" element={<Navigate to="/marketing/saas" replace />} />
          <Route path="/marketing/cloud" element={<MarketingCloud />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/contact" element={<Navigate to="/today" replace />} />
          <Route path="/upgrade" element={<UpgradePage />} />
          <Route path="/billing/mock-checkout" element={<MockCheckoutPage />} />
          <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />

          <Route element={<RequireAuth />}>
            <Route element={<AppShell />}>
            <Route path="/today" element={<BackendTodayPage />} />
            <Route path="/today-demo" element={<Navigate to="/today" replace />} />
            <Route path="/signals" element={<RncDashboard />} />
            <Route path="/signals/rnc/:id" element={<RncDetailPage />} />
            <Route path="/signals/rnc/:id/workflow" element={<RncWorkflowViewer />} />
            <Route path="/signals/abm/:id" element={<AbmDetailPage />} />
            <Route path="/signals/abm/:id/workflow" element={<AbmWorkflowViewer />} />
            <Route path="/signals/live" element={<SignalsPage />} />
            <Route path="/signals/live/:id" element={<SignalDetailPage />} />
            <Route path="/signals/sources" element={<SignalsSourcesPage />} />
            <Route path="/signals/reddit" element={<Navigate to="/signals" replace />} />
            <Route path="/signals/quora" element={<Navigate to="/signals" replace />} />
            <Route path="/signals/linkedin" element={<Navigate to="/signals" replace />} />
            <Route path="/signals/x" element={<Navigate to="/signals" replace />} />
            <Route path="/signals/communities" element={<Navigate to="/signals" replace />} />
            <Route path="/signals/competitors" element={<Navigate to="/signals" replace />} />
            <Route path="/signals/rules" element={<Navigate to="/signals" replace />} />
            <Route path="/reports" element={<Navigate to="/reports/leads" replace />} />
            <Route path="/reports/leads" element={<LeadReportsPage />} />
            <Route path="/usage" element={<UsagePage />} />
            <Route path="/dashboard" element={<Navigate to="/today" replace />} />

            <Route path="/settings" element={<SettingsHomeRedirect />} />
            <Route path="/settings/team" element={<SettingsTeam />} />
            <Route path="/settings/instance" element={<SettingsExtendedRoute><SettingsInstance /></SettingsExtendedRoute>} />
            <Route path="/settings/billing" element={<SettingsExtendedRoute><SettingsBilling /></SettingsExtendedRoute>} />
            <Route path="/settings/account" element={<SettingsAccount />} />
            <Route path="/settings/persona" element={<SettingsPersona />} />
            <Route path="/settings/agents" element={<SettingsAiAgents />} />
            <Route path="/settings/ai-agents" element={<SettingsAiAgents />} />
            <Route path="/settings/connections" element={<SettingsConnections />} />
            <Route path="/settings/general" element={<SettingsGeneral />} />
            <Route path="/settings/safety" element={<SettingsExtendedRoute><SettingsSafety /></SettingsExtendedRoute>} />
            <Route path="/settings/performance" element={<SettingsExtendedRoute><SettingsPerformance /></SettingsExtendedRoute>} />
            <Route path="/settings/privacy" element={<SettingsExtendedRoute><SettingsPrivacy /></SettingsExtendedRoute>} />
            <Route path="/settings/audit" element={<SettingsExtendedRoute><SettingsAudit /></SettingsExtendedRoute>} />
            <Route path="/settings/routing" element={<SettingsExtendedRoute><SettingsRouting /></SettingsExtendedRoute>} />
            <Route path="/settings/referrals" element={<SettingsExtendedRoute><SettingsReferrals /></SettingsExtendedRoute>} />

            <Route path="/inbox" element={<BackendInbox />} />
            <Route path="/inbox/:conversationId" element={<BackendInbox />} />
            <Route path="/inbox-demo" element={<Navigate to="/inbox" replace />} />
            <Route path="/inbox-demo/:conversationId" element={<Navigate to="/inbox" replace />} />
            <Route path="/inbound" element={<ToInbox />} />
            <Route path="/inbound/*" element={<ToInbox />} />

            <Route path="/outreach" element={<OutreachPage />} />
            <Route path="/outreach/dashboard" element={<OutboundDashboard />} />
            <Route path="/outreach/campaigns" element={<OutreachPage />} />
            <Route path="/outreach/campaigns/live" element={<OutboundCampaigns />} />
            <Route path="/outreach/campaigns/new" element={<CampaignWizard />} />
            <Route path="/outreach/design-campaigns/:id" element={<DesignCampaignRedirect />} />
            <Route path="/outreach/campaigns/:id/edit" element={<CampaignEditor />} />
            <Route path="/outreach/campaigns/:id" element={<CampaignEditor />} />
            <Route path="/outreach/workflow-analytics" element={<WorkflowAnalyticsPage />} />
            <Route path="/outreach/templates" element={<OutreachTemplatesPage />} />
            <Route path="/outreach/sequences/:id" element={<SequenceBuilder />} />
            <Route path="/outreach/linkedin" element={<Navigate to="/outreach" replace />} />
            <Route path="/outreach/email" element={<Navigate to="/outreach" replace />} />
            <Route path="/outreach/whatsapp" element={<Navigate to="/outreach" replace />} />
            <Route path="/outreach/sms" element={<Navigate to="/outreach" replace />} />
            <Route path="/outreach/voice" element={<Navigate to="/outreach" replace />} />
            <Route path="/outbound" element={<ToOutreach />} />
            <Route path="/outbound/*" element={<ToOutreach />} />

            <Route path="/people" element={<BackendPeoplePage />} />
            <Route path="/people-demo" element={<Navigate to="/people" replace />} />
            <Route path="/people/:id" element={<BackendPeoplePage />} />
            <Route path="/people/deals" element={<Navigate to="/people" replace />} />
            <Route path="/crm" element={<ToPeople />} />
            <Route path="/crm/leads" element={<ToPeople />} />
            <Route path="/crm/leads/:id" element={<ToPeople />} />
            <Route path="/crm/accounts" element={<ToPeople />} />
            <Route path="/crm/accounts/:id" element={<ToPeople />} />
            <Route path="/crm/contacts" element={<ToPeople />} />
            <Route path="/crm/opportunities" element={<ToDeals />} />
            <Route path="/crm/opportunities/:id" element={<ToDeals />} />
            <Route path="/crm/activities" element={<ToPeople />} />

            <Route path="/brain" element={<KnowledgeOverview />} />
            <Route path="/brain/documents" element={<BrainDocumentsRoute />} />
            <Route path="/brain/memory" element={<ChunkingConfig />} />
            <Route path="/brain/chunking" element={<Navigate to="/brain/memory" replace />} />
            <Route path="/brain/embeddings" element={<Navigate to="/brain/memory" replace />} />
            <Route path="/brain/playground" element={<RagPlayground />} />
            <Route path="/brain/voice-memos" element={<VoiceMemosPage />} />
            <Route path="/knowledge" element={<ToBrain />} />
            <Route path="/knowledge/*" element={<ToBrain />} />

            <Route path="/content" element={<ContentRoute><ContentPlaceholder /></ContentRoute>} />
            <Route path="/content/studio" element={<ContentRoute><ContentPlaceholder /></ContentRoute>} />
            <Route path="/content/queue" element={<ContentRoute><ContentPlaceholder /></ContentRoute>} />
            <Route path="/content/library" element={<ContentRoute><ContentPlaceholder /></ContentRoute>} />
            <Route path="/content/brand-voice" element={<ContentRoute><ContentPlaceholder /></ContentRoute>} />
            <Route path="/content/distribution" element={<ContentRoute><ContentPlaceholder /></ContentRoute>} />

            <Route path="/agents" element={<Navigate to="/settings/agents" replace />} />
            <Route path="/agents/*" element={<Navigate to="/settings/agents" replace />} />
            <Route path="/admin" element={<Navigate to="/today" replace />} />
            <Route path="/admin/*" element={<Navigate to="/today" replace />} />
            <Route path="/analytics" element={<Navigate to="/settings/performance" replace />} />
            <Route path="/analytics/*" element={<Navigate to="/settings/performance" replace />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AppStoreProvider>
  );
}

export default App;
